/**
 * Переход по Приглашению.
 *
 * Пространство и одноразовый ключ пришли в самой ссылке, почта известна серверу из
 * Приглашения — поэтому здесь спрашивают только имя и пароль. Просить человека вписать
 * фирму и почту заново значило бы дать ему возможность ошибиться в том, что уже известно.
 *
 * Пароль человек задаёт себе сам: Администратор его не знает и знать не должен. Отказ
 * не уточняет, что именно не так со ссылкой, — по разнице ответов «ссылки нет» и «ссылка
 * уже сработала» перебором вычисляют, кого звали в фирму. Единственное исключение —
 * короткий пароль: это ошибка самого человека, и молчать о ней значит запереть его на
 * форме, где он не понимает, чего от него хотят.
 */
import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Button, Field, Notice } from '../ui/controls.js';
import { useT } from '../state/settings.js';
import { createApi, ApiError } from '../net/api.js';

export function JoinInvite({
  spaceSlug,
  token,
  onJoined,
  onSignIn,
}: {
  readonly spaceSlug: string;
  readonly token: string;
  readonly onJoined: (spaceSlug: string) => void;
  readonly onSignIn: () => void;
}): ReactNode {
  const t = useT();
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [working, setWorking] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!displayName.trim() || !password) {
      setRefusal(t('join.needAll'));
      return;
    }
    setWorking(true);
    setRefusal(null);
    try {
      await createApi(spaceSlug).acceptInvitation({
        token,
        displayName: displayName.trim(),
        password,
      });
      // Ожидание снимаем до ухода с экрана: не уйди мы отсюда почему-либо, человек
      // остался бы перед запертой кнопкой без единого слова о том, что произошло.
      setWorking(false);
      onJoined(spaceSlug);
    } catch (error) {
      const weak = error instanceof ApiError && error.code === 'weak_password';
      setRefusal(weak ? t('join.passwordShort') : t('join.failed'));
      setWorking(false);
    }
  };

  return (
    <main id="main" className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 p-6">
      <h1 className="text-2xl font-semibold">{t('app.name')}</h1>

      <form onSubmit={submit} className="flex flex-col gap-4">
        <h2 className="text-lg">{t('join.title')}</h2>
        <p className="text-sm text-ink-soft">{t('join.intro', { space: spaceSlug })}</p>

        <Field
          label={t('join.name')}
          value={displayName}
          autoComplete="name"
          onChange={(event) => setDisplayName(event.currentTarget.value)}
        />

        <Field
          label={t('join.password')}
          hint={t('join.passwordHint')}
          type="password"
          value={password}
          autoComplete="new-password"
          onChange={(event) => setPassword(event.currentTarget.value)}
        />

        {refusal ? <Notice kind="error">{refusal}</Notice> : null}

        <Button
          type="submit"
          look="primary"
          disabled={working}
          className="justify-center py-2 text-base"
        >
          {working ? t('join.working') : t('join.submit')}
        </Button>

        {/* Ссылка сработала один раз — второй переход по ней приведёт сюда же, и человеку
            нужен выход ко входу, а не тупик с непонятным отказом. */}
        <Button look="quiet" onClick={onSignIn} className="justify-center">
          {t('join.toSignIn')}
        </Button>
      </form>
    </main>
  );
}
